import { useState } from "react";
import { HStack } from "../../../../../ui/hstack";
import { Input, InputField } from "../../../../../ui/input";
import { Skeleton } from "../../../../../ui/skeleton";
import { Text } from "../../../../../ui/text";
import { VStack } from "../../../../../ui/vstack";
import Chip from "../Chip";
import type { FieldControlProps } from "../fieldTypes";

const SKELETON_CHIPS = ["w-28", "w-36", "w-24"];

/**
 * Pick one, as pills.
 *
 * A select whose options are known when the card is drawn is just the pills. One whose
 * options are computed later (`field.optionsStatus` is not "ready" — the Brief's
 * purchase driver, which is a model call) has more to say:
 *
 *  - while they load: three skeleton pills and a caption, so the wait has a shape;
 *  - once they land: the pills plus an "Otro: escribe el tuyo" pill, because a
 *    suggestion the person disagrees with must never be the only way forward;
 *  - if they never come (failed, or the card's own timeout): the skeleton simply goes
 *    away and the free-text box is all there is. Not an error — the field is optional
 *    and the concepts infer the driver when it is blank.
 *
 * The value is the option's `value`, or whatever they typed for "Otro". Both are just
 * text to the backend.
 */
const SingleSelectField = ({
  field,
  value,
  onChange,
  disabled,
  invalid,
  options,
  onBlur,
}: FieldControlProps) => {
  const dynamic = field.optionsStatus !== "ready";
  const text = typeof value === "string" ? value : "";
  const known = options.options.some((option) => option.value === text);
  // Their own words are "Otro" even if the options that were on screen when they typed
  // have since been replaced.
  const [customOpen, setCustomOpen] = useState(!!text && !known);

  if (dynamic && options.status === "loading") {
    return (
      <VStack space="sm" aria-busy>
        <HStack space="sm">
          {SKELETON_CHIPS.map((width) => (
            <Skeleton key={width} className={`h-11 rounded-full ${width}`} />
          ))}
        </HStack>
        <Text className="text-xs text-typography-500">
          Estamos sugiriendo motivos de compra…
        </Text>
      </VStack>
    );
  }

  const showCustom =
    dynamic &&
    (customOpen || options.status !== "ready" || !options.options.length);

  return (
    <VStack space="sm">
      {options.options.length ? (
        <HStack
          role="radiogroup"
          aria-label={field.label}
          className="flex-row flex-wrap gap-2"
        >
          {options.options.map((option) => (
            <Chip
              key={option.value}
              role="radio"
              selected={text === option.value && !customOpen}
              disabled={disabled}
              onPress={() => {
                setCustomOpen(false);
                // Picking the chosen pill again clears it — the field is optional.
                onChange(text === option.value ? undefined : option.value);
              }}
            >
              {option.label}
            </Chip>
          ))}
          {dynamic ? (
            <Chip
              role="radio"
              selected={customOpen}
              disabled={disabled}
              onPress={() => {
                setCustomOpen(true);
                if (known) onChange(undefined);
              }}
            >
              Otro: escribe el tuyo
            </Chip>
          ) : null}
        </HStack>
      ) : null}

      {showCustom ? (
        <Input size="md" isDisabled={disabled} isInvalid={invalid}>
          <InputField
            value={known ? "" : text}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="Escribe el motivo de compra"
            accessibilityLabel={`${field.label} (escribe el tuyo)`}
          />
        </Input>
      ) : null}
    </VStack>
  );
};

export default SingleSelectField;
